import { useQuery } from '@tanstack/react-query';
import type { ReservationState } from '@swi/contracts';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api/endpoints';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Pill,
  Select,
  Skeleton,
  STATE_LABEL,
  StateBadge,
  buttonClass,
} from '../components/ui/primitives';
import { useAuth } from '../lib/auth';
import { dateTime, duration, money, relative } from '../lib/format';

const STATES: ReservationState[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'CONFIRMED',
  'IN_FLIGHT',
  'COMPLETED',
  'CANCELLED',
  'REJECTED',
  'EXPIRED',
];

export function MyReservationsPage() {
  const { isStaff } = useAuth();
  const [state, setState] = useState<ReservationState | ''>('');
  const [mine, setMine] = useState(!isStaff);
  const [page, setPage] = useState(1);

  const reservations = useQuery({
    queryKey: ['reservations', { state, mine, page }],
    queryFn: () =>
      api.listReservations({
        state: state === '' ? undefined : state,
        mine: mine ? true : undefined,
        page,
        pageSize: 20,
      }),
  });

  return (
    <>
      <PageHeader
        title={isStaff && !mine ? 'Všechny rezervace' : 'Moje rezervace'}
        description="Stav, termín, trasa a cena. Storno je možné do začátku letu."
        actions={
          <Link to="/reservations/new" className={buttonClass('primary', 'sm')}>
            Nová rezervace
          </Link>
        }
      />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Stav">
            <Select
              value={state}
              onChange={(event) => {
                setState(event.target.value as ReservationState | '');
                setPage(1);
              }}
            >
              <option value="">Všechny stavy</option>
              {STATES.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {STATE_LABEL[candidate]}
                </option>
              ))}
            </Select>
          </Field>

          {isStaff && (
            <Field label="Rozsah">
              <Select
                value={mine ? 'mine' : 'all'}
                onChange={(event) => {
                  setMine(event.target.value === 'mine');
                  setPage(1);
                }}
              >
                <option value="all">Celá půjčovna</option>
                <option value="mine">Jen moje</option>
              </Select>
            </Field>
          )}

          <div className="flex items-end">
            <Pill tone="neutral">
              {reservations.data ? `${String(reservations.data.total)} rezervací` : '…'}
            </Pill>
          </div>
        </div>
      </Card>

      {reservations.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-20" />
          ))}
        </div>
      ) : (reservations.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title="Žádná rezervace"
          description="Vyber vzducholoď v katalogu a založ první rezervaci."
          action={
            <Link to="/fleet" className={buttonClass('outline', 'sm')}>
              Katalog flotily
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {reservations.data?.items.map((reservation) => (
            <li key={reservation.id}>
              <Link
                to={`/reservations/${reservation.id}`}
                className="glass flex flex-wrap items-center gap-4 p-4 transition-colors hover:bg-[var(--surface-glass-strong)]"
              >
                <div className="min-w-32">
                  <p className="mono text-[13px] font-semibold text-[var(--accent)]">
                    {reservation.airshipRegistration}
                  </p>
                  <p className="text-[12px] text-[var(--text-secondary)]">
                    {reservation.airshipName}
                  </p>
                </div>

                <div className="min-w-40 flex-1">
                  <p className="text-[13px]">
                    <span className="mono">{reservation.departureHubCode}</span> →{' '}
                    <span className="mono">{reservation.arrivalHubCode}</span>
                  </p>
                  <p className="text-[12px] text-[var(--text-secondary)]">
                    {dateTime(reservation.startAt)} · {duration(reservation.startAt, reservation.endAt)}
                  </p>
                </div>

                <div className="min-w-24">
                  <p className="text-[12px] text-[var(--text-muted)]">
                    {relative(reservation.startAt)}
                  </p>
                  <p className="text-[12px]">{reservation.passengers} pax</p>
                </div>

                <div className="min-w-28 text-right">
                  <p className="mono text-[13px] font-semibold">{money(reservation.priceCzk)}</p>
                  {!mine && (
                    <p className="text-[11px] text-[var(--text-muted)]">{reservation.renterName}</p>
                  )}
                </div>

                <StateBadge state={reservation.state} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {(reservations.data?.total ?? 0) > 20 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            ← Předchozí
          </Button>
          <span className="mono text-[12px] text-[var(--text-muted)]">strana {page}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={reservations.data?.hasNext !== true}
            onClick={() => setPage(page + 1)}
          >
            Další →
          </Button>
        </div>
      )}
    </>
  );
}
